/**
 * Bot Commands — pure functions for team leader orders.
 * Human players issue commands to bot teammates during aiming phase.
 * Commands cost movement and influence bot behavior on their next turn.
 */

import type {
  GameState,
  PlayerState,
  BotCommand,
  BotCommandType,
  WeaponKey,
  Vec3,
} from '../types';
import { COMMAND_MOVEMENT_COST, COMMAND_MAX_PER_TURN, COMMAND_EXPIRY_TURNS } from '../constants';

// =============================================================================
// QUERIES
// =============================================================================

/** Check if a player can issue commands (human, alive, has movement, has alive bot teammates). */
export function canIssueCommands(player: PlayerState, gameState: GameState): boolean {
  if (player.isBot) return false;
  if (!player.alive) return false;
  if (player.movementRemaining < COMMAND_MOVEMENT_COST) return false;
  return getBotTeammates(player, gameState).length > 0;
}

/** Get alive bot teammates for a player. */
export function getBotTeammates(player: PlayerState, gameState: GameState): PlayerState[] {
  return gameState.players.filter(
    p => p.id !== player.id && p.alive && p.isBot && p.teamIndex === player.teamIndex
  );
}

/** Count commands issued by a player this turn (for rate limiting). */
export function commandsIssuedThisTurn(
  playerId: number,
  turnNumber: number,
  commands: BotCommand[]
): number {
  return commands.filter(
    c => c.issuedByPlayerId === playerId && c.issuedTurn === turnNumber
  ).length;
}

// =============================================================================
// MUTATIONS (pure — return new GameState)
// =============================================================================

/**
 * Issue a command to a bot teammate.
 * Returns new GameState or null if invalid.
 */
export function issueCommand(
  gameState: GameState,
  commanderId: number,
  botId: number,
  commandType: BotCommandType,
  params?: {
    targetPlayerId?: number;
    targetPlanetIndex?: number;
    weaponKey?: WeaponKey;
    surfaceDir?: Vec3;
    holdTurns?: number;
    holdPreset?: string;
  }
): GameState | null {
  const commander = gameState.players.find(p => p.id === commanderId);
  const bot = gameState.players.find(p => p.id === botId);

  if (!commander || !bot) return null;
  if (commander.isBot) return null;
  if (!commander.alive) return null;
  if (!bot.alive || !bot.isBot) return null;
  if (bot.teamIndex !== commander.teamIndex) return null;
  if (commander.movementRemaining < COMMAND_MOVEMENT_COST) return null;

  // Rate limit
  const issued = commandsIssuedThisTurn(commanderId, gameState.turnNumber, gameState.botCommands);
  if (issued >= COMMAND_MAX_PER_TURN) return null;

  const command: BotCommand = {
    type: commandType,
    issuedByPlayerId: commanderId,
    issuedTurn: gameState.turnNumber,
    targetPlayerId: params?.targetPlayerId,
    targetPlanetIndex: params?.targetPlanetIndex,
    weaponKey: params?.weaponKey,
    surfaceDir: params?.surfaceDir,
    holdTurns: params?.holdTurns,
    holdPreset: params?.holdPreset,
    executed: false,
  };

  // Update players: deduct movement from commander, set pendingCommand on bot
  const newPlayers = gameState.players.map(p => {
    if (p.id === commanderId) {
      return { ...p, movementRemaining: p.movementRemaining - COMMAND_MOVEMENT_COST };
    }
    if (p.id === botId) {
      return { ...p, pendingCommand: command };
    }
    return p;
  });

  return {
    ...gameState,
    players: newPlayers,
    botCommands: [...gameState.botCommands, command],
  };
}

/**
 * Cancel a pending command on a bot. No movement refund.
 */
export function cancelCommand(gameState: GameState, botId: number): GameState {
  const newPlayers = gameState.players.map(p => {
    if (p.id === botId && p.pendingCommand) {
      const { pendingCommand: _, ...rest } = p;
      return rest as PlayerState;
    }
    return p;
  });

  return { ...gameState, players: newPlayers };
}

/**
 * Mark a bot's pending command as executed and clear it.
 */
export function markCommandExecuted(gameState: GameState, botId: number): GameState {
  const newPlayers = gameState.players.map(p => {
    if (p.id === botId && p.pendingCommand) {
      const { pendingCommand: _, ...rest } = p;
      return rest as PlayerState;
    }
    return p;
  });

  return { ...gameState, players: newPlayers };
}

/**
 * Decrement holdTurns on holdPosition commands.
 * Called at the start of the bot's turn. Returns new GameState.
 * When holdTurns reaches 0, clears the pendingCommand.
 */
export function decrementHoldTurns(gameState: GameState, botId: number): GameState {
  const newPlayers = gameState.players.map(p => {
    if (p.id !== botId || !p.pendingCommand) return p;
    if (p.pendingCommand.type !== 'holdPosition') return p;

    const remaining = (p.pendingCommand.holdTurns ?? 1) - 1;
    if (remaining <= 0) {
      // Hold expired — clear the command
      const { pendingCommand: _, ...rest } = p;
      return rest as PlayerState;
    }
    return { ...p, pendingCommand: { ...p.pendingCommand, holdTurns: remaining } };
  });

  return { ...gameState, players: newPlayers };
}

/**
 * Remove expired/invalid commands.
 * Clears pendingCommand on bots with stale orders.
 */
export function cleanupStaleCommands(gameState: GameState): GameState {
  const validCommands = gameState.botCommands.filter(cmd => {
    // Expired by age
    if (gameState.turnNumber - cmd.issuedTurn > COMMAND_EXPIRY_TURNS) return false;

    // Target player dead
    if (cmd.targetPlayerId !== undefined) {
      const target = gameState.players.find(p => p.id === cmd.targetPlayerId);
      if (target && !target.alive) return false;
    }

    return true;
  });

  // Clear pendingCommand on any bot whose command is stale
  const newPlayers = gameState.players.map(p => {
    if (!p.pendingCommand) return p;

    const cmd = p.pendingCommand;

    // Expired by age
    if (gameState.turnNumber - cmd.issuedTurn > COMMAND_EXPIRY_TURNS) {
      const { pendingCommand: _, ...rest } = p;
      return rest as PlayerState;
    }

    // Bot dead
    if (!p.alive) {
      const { pendingCommand: _, ...rest } = p;
      return rest as PlayerState;
    }

    // Target player dead
    if (cmd.targetPlayerId !== undefined) {
      const target = gameState.players.find(tp => tp.id === cmd.targetPlayerId);
      if (target && !target.alive) {
        const { pendingCommand: _, ...rest } = p;
        return rest as PlayerState;
      }
    }

    return p;
  });

  return {
    ...gameState,
    players: newPlayers,
    botCommands: validCommands,
  };
}
